/** Seniority levels used by the Create-Stelle form (MVP_requirements.md §4 Screen 2). */
export const SENIORITY_LEVELS = ["Junior", "Mid", "Senior", "Lead", "Executive"] as const;
export type Seniority = (typeof SENIORITY_LEVELS)[number];

/** Beschäftigungsart — HR-standard contract/engagement axis. German lower-case
 * slugs match the backend's `EmploymentType` TextChoices verbatim. */
export const EMPLOYMENT_TYPES = [
  "vollzeit",
  "teilzeit",
  "freiberuflich",
  "werkvertrag",
  "praktikum",
  "werkstudent",
  "befristet",
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

/** German labels for `EmploymentType`. UI components render via this map; the
 * code-language slug is what we serialise to the backend. */
export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  vollzeit: "Vollzeit",
  teilzeit: "Teilzeit",
  freiberuflich: "Freiberuflich",
  werkvertrag: "Werkvertrag",
  praktikum: "Praktikum",
  werkstudent: "Werkstudent",
  befristet: "Befristet",
};

/** One scoring criterion attached to a job — Option D of the
 * Gregor-parity audit. Unified concept: covers hard skills, soft
 * skills, formal requirements (Führerschein, Ausbildung) and job-context
 * factors (Office-Präsenz, sofortiger Start) alike. The frontend
 * shows them in a single "Anforderungen" list; the scorer reads them
 * uniformly with `importance` + `required` as the weighting axes. */
/** Arbeitsmodell — where the work happens (#49). Recruiters used to write it
 * into the Standort ("Düsseldorf, Deutschland (Hybrid)"). */
export const WORK_MODELS = ["onsite", "hybrid", "remote"] as const;
export type WorkModel = (typeof WORK_MODELS)[number];

export const WORK_MODEL_LABELS: Record<WorkModel, string> = {
  onsite: "Vor Ort",
  hybrid: "Hybrid",
  remote: "Remote",
};

/** One place suggested for the Standort while typing (#49). `label` is what
 * the job keeps ("Münster, Deutschland"); `region` tells look-alike towns
 * apart in the list. */
export interface PlaceSuggestion {
  name: string;
  region: string | null;
  country: string | null;
  label: string;
  lat: number;
  lng: number;
}

export interface JobCriterion {
  name: string;
  /** 1-10. Higher = more weight in the score. */
  importance: number;
  /** Knock-out flag — `true` means the candidate is filtered out when
   * the criterion is missing (must-have). `false` is nice-to-have. */
  required: boolean;
  /** Optional free-text context the scorer reads with the criterion.
   * E.g. "Office-Präsenz" → description "3 Tage/Woche". Empty string
   * when not set. */
  description: string;
}

/** Stelle-Status lifecycle (MVP_requirements.md §5.1).
 *
 * Transitions are derived from candidate progress except `closed` which is a
 * manual button. English identifiers here; the backend may persist German
 * labels, but the domain model stays in code language (CLAUDE.md §1). */
export const JOB_STATUSES = [
  "draft",
  "kandidatensuche",
  "longlist",
  "outreaching",
  "empfehlung",
  "placed",
  "closed",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export interface Job {
  id: string;
  /** The user who created the job (Recruiter in the SaaS case, Admin in the
   * Agency case — MVP_requirements.md §3). */
  createdBy: string;
  /** When the admin released the recommendation; null until then. Kept when
   * the job is closed, so the recruiter keeps seeing it (ADR 0004). */
  recommendationReleasedAt: string | null;
  /** Additional recruiters the admin has explicitly given access to. The
   * owner (`createdBy`) is never in this list. */
  assignedRecruiters: string[];
  title: string;
  /** Customer company. Free-form. May differ from the recruiter's own
   * company when headhunters work for multiple clients. */
  company: string;
  location: string;
  workModel: WorkModel | null;
  /** Branche of the hiring company — free text, populated by the parser. */
  industry: string;
  /** 1–3 sentence summary of the role context. NOT a marketing intro and NOT
   * the full posting — the structured `tasks`/`requirements`/`benefits` blocks
   * carry the substance. */
  description: string;
  /** Concrete responsibilities, one per item. Bullet-list rendered in UI. */
  tasks: readonly string[];
  /** Concrete benefits, one per item. */
  benefits: readonly string[];
  /** Recruiter's free-form prioritisation hints — sentences, not tags.
   * Kept as a free-text briefing field; can be migrated into criteria
   * later if the recruiter wants them gewichtet. */
  hiringPriorities: readonly string[];
  /** Unified scoring criteria. One flat list — knock-out toggle picks
   * must vs. should, importance is the weight, description is optional
   * context. Replaces the previous hard/soft/custom-factors split
   * (Option D of the Gregor-parity audit). */
  criteria: readonly JobCriterion[];
  /** Whether candidate distances are computable for this job — true only
   * when the backend resolved the job's location to coordinates. False
   * means NO candidate has a distance, so the radius filter is skipped
   * rather than applied (it would hide every candidate, not narrow them). */
  distanceAvailable: boolean;
  seniority: Seniority | null;
  employmentType: EmploymentType | null;
  salaryFrom: number | null;
  salaryTo: number | null;
  /** Targeted hire date — ISO `YYYY-MM-DD`. Null when "frühestmöglich" or
   * the posting is open-ended. */
  targetStartDate: string | null;
  notes: string | null;
  status: JobStatus;
  /** Original source of the import, if any. */
  sourceUrl: string | null;
  createdAt: string;
  updatedAt: string;
}
